<template>
  <div class="wrapper" @mouseenter="overlay = true" @mouseleave="overlay = false">

    <div v-bind:class="{ 'd-none': remoteCameraOn }" class="audio-image">
        <img src="@/assets/call-background.png" class="audio-image-img"/>
      </div>
    
    <v-row justify="center" align="center">      
      <!-- ring audio -->
      <audio ref="ring" src="@/assets/ring.wav" loop></audio>

      <div class="video-source">
          <video 
              playsinline 
              ref="vidSource" 
              height="200"
              width="200"
              class="video-source-content">
          </video>
      </div>

      <div class="canvas-source">
          <canvas 
              ref="canvasSource"
              height="200"
              width="200"
              class="canvas-source-content">
          </canvas>
      </div>

      <!-- loading bar -->
      <v-progress-linear v-if="loading" indeterminate :absolute="!loading" top></v-progress-linear>

      <section id="video-container">
        <div v-bind:class="{ 'd-none': remoteCameraOn }" class="audio-image">
          <img src="@/assets/call-background.png" class="audio-image-img"/>
        </div>
        <img src="@/assets/call-background-flip.png" class="image-background"  />
        <img src="@/assets/call-background.png" class="image-background" ref="imageBackground"/>
        <div id="local-video" v-bind:class="{ 'd-none': !localCameraOn }"></div>  
        <div class="fallbackImage" v-bind:class="{ 'd-none': remoteCameraOn }">
          <div class="row fill-height align-center justify-center">
              <v-img src="@/assets/images/medgate-logo.png"></v-img>
          </div>
        </div>
      </section>

      <img src="@/assets/call-background.png" class="image-waiting-background" v-bind:class="{'d-none': isConnected}" />

      <div id="remote-video" v-bind:class="{ 'd-none': !remoteCameraOn }"></div>
      
      <v-overlay absolute :value="overlay">

        <v-card height="100vh" width="100vw" class="black--text" color="transparent">

          <LoadingContent :consult="consult" :loading="loading" :status="status" />

          <!-- footer -->
          <v-footer absolute height="100" color="transparent">
            <v-row :justify="$vuetify.breakpoint.smAndDown ? 'start' : 'center'" align="center">
              <!-- toggle publisher audio button -->
              <MuteButton :isConnected="isConnected" :publisherAudio="publisherAudio" @togglePublisherAudio="togglePublisherAudio" />            
              <!-- close/end button -->
              <HangupButton :cancellingCall="cancellingCall" :isConnected="isConnected" @hangUp="hangUp" />
              <!-- toggle publisher video button -->
              <VideoButton :isConnected="isConnected" :display="consult.call_method == 'video'" :publisherVideo="publisherVideo" @togglePublisherVideo="togglePublisherVideo" />
            </v-row>
          </v-footer>

        </v-card>
      </v-overlay>

    </v-row>
  </div>
</template>

<script>

import '@tensorflow/tfjs'
import moment from 'moment'
import AgoraRTC from 'agora-rtc-sdk-ng'

import MuteButton from './components/MuteButton.vue'
import HangupButton from './components/HangupButton.vue'
import VideoButton from './components/VideoButton.vue'
import LoadingContent from './components/LoadingContent.vue'

import API from '@/api';
import commonPusher from "@/views/csr/common/pusher";
const callRepo = API.get('Call')
const caseRepo = API.get('Cases')

const bodyPix = require('@tensorflow-models/body-pix')

export default {
  name: 'Call',

  components: {MuteButton, HangupButton, VideoButton, LoadingContent},

  data: () => ({
    startTime: null,
    cancellingCall: false,
    isConnected: false,
    consult: JSON.parse(localStorage.getItem('consultation')),
    token: localStorage.getItem('auth_token'),
    logs: [],
    overlay: false,
    loading: false,
    status: '',
    publisherAudio: true,
    publisherVideo: true,
    callLogId: null,
    client: null,
    agora_id: process.env.VUE_APP_AGORA_ID,
    localStream: {
      videoTrack: null,
      audioTrack: null
    },
    remoteCameraOn: true,
    localCameraOn: true,
    channelName: '',

    callStatusOnUnload: '',

    model: null,
    offCanvas: null,
    offCtx: null,
    ctx: null,
    foregroundColor: {r: 0, g: 0, b: 0, a: 255},
    backgroundColor: {r: 0, g: 0, b: 0, a: 0},
    height: 400,
    width: 400,
    rejected: false
  }),

  async mounted() {

    window.addEventListener('beforeunload', this.isForcedClosed);

    if (this.consult.call_method == 'video') {
      this.model = await this.loadModel()
      const video = await this.setupCamera();
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      video.width = videoWidth;
      video.height = videoHeight;
      video.play();

      const canvas = this.$refs.canvasSource;
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      const ctx = canvas.getContext('2d');
      this.ctx = ctx;

      this.offCanvas = new OffscreenCanvas(videoWidth, videoHeight);
      this.offCtx = this.offCanvas.getContext('2d');

      this.width = videoWidth;
      this.height = videoHeight;

      this.renderVideo();
    } else {
      this.remoteCameraOn = false;
      this.localCameraOn = false;
    }

    await this.$store.dispatch('profile/updateStatus', 'In call');
    
    this.startTime = moment();
    this.logs.push("Attempting to start Agora Session");
    this.status = 'Calling...';
    this.loading = true;
    let channel = this.$store.getters['profile/fullName'].replaceAll(' ', '') + '-' + this.consult.id;
    this.channelName = channel;

    localStorage.setItem('call_channel', channel);
    this.logs.push("Session Channel Name " + channel);

    await this.initializePusher(channel)

    let agora = await callRepo.getToken(channel)
    this.loading = true;
    this.isConnected = false;
    // let response = await callRepo.callUser(this.consult.id, this.consult.patient.id, channel);
    await callRepo.recordCall(channel, 'acquire', this.consult.patient.id);
    // this.callLogId = response.data.id
    // await this.updateCallStatus('intiated')
    // console.log("[call user response]", response);
    // this.$refs.ring.play();
    this.initializeAgora(agora.data.token, agora.data.channel);
  },

  methods: {

    isForcedClosed () {
      // ? 'closed_connected' : 'closed_ringing'
      this.client.leave();
      if (this.localStream.videoTrack != null) {
        this.localStream.videoTrack.close();
        this.localStream.videoTrack = null;
      }

      if (this.localStream.audioTrack != null) {
        this.localStream.audioTrack.close();
        this.localStream.audioTrack = null;
      }

      if (this.rejected) {
        localStorage.setItem('call_window_closed', 'closed_rejected');
      } else if (!this.isConnected) {
        localStorage.setItem('call_window_closed', 'closed_ringing');
      } else if (this.callStatusOnUnload != 'hanged_up') {
        localStorage.setItem('call_window_closed', 'connected_closed');
      }
    },

    async initializePusher (channelName) {
      let self = this;
      const channel = commonPusher.getPusher().subscribe(channelName);
      channel.bind('end-call', async (data) => {
        if (!self.isConnected) {
          await self.updateCallStatus('rejected')
          self.rejected = true;
          self.$refs.ring.pause();
          console.log("rejected: ", data);
          window.close();
        }
      });
    },

    async renderVideo () {
      const segmentation = await this.model.segmentPerson(this.$refs.vidSource, {
        flipHorizontal: false,
        internalResolution: 'medium',
        segmentationThreshold: 0.7,
        nmsRadius: 10
      });

      const personMasked = bodyPix.toMask(segmentation, this.foregroundColor, this.backgroundColor);
      bodyPix.drawBokehEffect(this.$refs.canvasSource, this.$refs.vidSource, personMasked, 20, 20, false)

      this.ctx.drawImage(this.$refs.imageBackground, 0, 0, this.width, this.height);

      const oldGCO = this.offCtx.globalCompositeOperation
      this.offCtx.clearRect(0, 0, this.width, this.height);
      this.offCtx.putImageData(personMasked, 0, 0);
      this.offCtx.globalCompositeOperation = 'source-in';
      this.offCtx.drawImage(this.$refs.vidSource, 0, 0);

      this.offCtx.globalCompositeOperation = oldGCO;

      this.ctx.drawImage(this.offCanvas, 0, 0);

      requestAnimationFrame(this.renderVideo);
    },

    async setupCamera () {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error(
                'Browser API navigator.mediaDevices.getUserMedia not available');
        }

        const video = this.$refs.vidSource;
        const stream = await navigator.mediaDevices.getUserMedia({
            'audio': false,
            'video': true,
        });
        video.srcObject = stream;

        return new Promise((resolve) => {
            video.onloadedmetadata = () => {
                resolve(video);
            };
        });
    },

    async loadModel () {
      return await bodyPix.load({
                architecture: 'ResNet50',
                outputStride: 32,
                quantBytes: 2
            });
    },

    async initializeAgora (token, channel) {
      
      try {
        this.client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        console.log('Agora_Client_Create', this.client);

        await this.client.join(this.agora_id, channel, token, this.$store.getters['profile/profile'].id);

        console.log("User " + this.$store.getters['profile/profile'].id + " join channel successfully");

        let response = await callRepo.callUser(this.consult.id, this.consult.patient.id, channel);
        this.callLogId = response.data.id
        this.$refs.ring.play();

        this.createLocalStream();
        this.initializedAgoraListeners();  
      } catch (error) {
        console.log("Agora init failed", error);
      }
      await callRepo.recordCall(this.channelName, 'start', this.consult.patient.id);
    },

    async createLocalStream() {

      this.localStream.audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: "music_standard"
      });

      if (this.consult.call_method == 'video') {
        const canvasDom = this.$refs.canvasSource;
        let canvasStream = navigator.userAgent.indexOf("Firefox") > -1 ? canvasDom.mozCaptureStream() : canvasDom.captureStream();
        this.localStream.videoTrack = AgoraRTC.createCustomVideoTrack({
            mediaStreamTrack: canvasStream.getVideoTracks()[0]
        });

        this.localStream.videoTrack.play("local-video");
      }
    },

    initializedAgoraListeners() {

      this.client.on("user-published", async (user, media) => {
        console.log('user subscribe: ', user);
        console.log('media subscribe: ', media);

        await this.client.subscribe(user, media);

        if (this.consult.call_method == 'video') {
          user.videoTrack.play('remote-video');
          this.remoteCameraOn = true;
        }
        user.audioTrack.play();

        if (!this.isConnected) {
          await this.updateCallStatus('connected')
          this.isConnected = true;
          this.loading = false;
          this.$refs.ring.pause();
          this.status = 'Connected';
          console.log('connected');

          if (this.consult.call_method == 'video') {
            await this.client.publish(Object.values(this.localStream));
          } else {
            await this.client.publish(this.localStream.audioTrack);
          }
          console.log(`Local Stream Published`);
        }
      });

      this.client.on("user-left", async (user) => {
        console.log("remote user left ", user.uid);
        await this.updateCallStatus('user ended')
        await callRepo.recordCall(this.channelName, 'stop', this.consult.patient.id);
        this.status = 'Call ended';
        window.close()
      });

      this.client.on("user-unpublished", (user, media) => {
        console.log('user-unpublished (user): ', user);
        console.log('user-unpublished (media): ', media);

        if (media == 'video') {
          this.remoteCameraOn = false;
        }

      });
    },

    async hangUp () {
      await this.$store.dispatch('profile/updateStatus', 'Online');
      if (this.isConnected) {
        this.status = 'Call ended';
        await this.updateCallStatus('completed')
      } else {
        await callRepo.endCall(this.channelName, true);
        this.status = 'Cancelled';
        await this.updateCallStatus('cancelled')
        this.$refs.ring.pause();
      }

      this.rejected = false;
      this.callStatusOnUnload = 'hanged_up';
      localStorage.setItem('call_window_closed', 'hanged_up');

      await callRepo.recordCall(this.channelName, 'stop', this.consult.patient.id);
      if (this.localStream.vidSource != null) {
        this.localStream.vidSource.close();
        this.localStream.vidSource = null;
      }

      if (this.localStream.audioTrack != null) {
        this.localStream.audioTrack.close();
        this.localStream.audioTrack = null;
      }
      this.client.leave();
      window.close();
    },

    async updateCallStatus(status='') {
      
      var endTime = moment()
      var duration = endTime.diff(this.startTime, 'seconds');

      const data = {
        _method: 'PUT',
        status: status,
        duration: duration,
        call_type: this.consult.call_method,
        case_call_id: this.callLogId
      }

      try {
        await caseRepo.updateCaseCallLog(data);
      } catch (error) {
        console.log(error)
      }
      
      console.log('[call-log]', data, duration, data.call_type, this.consult.call_method, this.consult, this.startTime, endTime)
    },

    togglePublisherVideo() {
      if (this.publisherVideo) {
        this.publisherVideo = false;
        this.localCameraOn = false;
        this.localStream.videoTrack.setMuted(true);
      } else {
        this.publisherVideo = true;
        this.localCameraOn = true;
        this.localStream.videoTrack.setMuted(false);
      }
    },

    togglePublisherAudio() {
      if (this.publisherAudio) {
        this.publisherAudio = false;
        this.localStream.audioTrack.setMuted(true);
      } else {
        this.publisherAudio = true;
        this.localStream.audioTrack.setMuted(false);
      }
    },
  }
}
</script>

<style lang="scss">
#remote-video {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: 1;
  margin: 0;
  padding: 0;
  cursor: pointer;

  div {
    video {
      object-fit: contain !important;
    }
  }
}

.fallbackImage {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: 1;
  margin: 0;
  padding: 0 150px;
}

#video-container {
  width: 320px;
    height: 200px;
    background-color: black;
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 2;
    border: 1px solid #fff;
}

#local-video {
  width: 100%;
    height: 100%;
    position: absolute;
    z-index: 5;
    cursor: pointer;
}

.canvas-source {
  width: 200px;
  position: absolute;
  top: 200px;
  left: -300px;
  background-color: aqua;
}

.video-source {
  height: 200px;
  width: 200px;
  position: absolute;
  top: 0;
  left: -300px;
}

.video-source-content {
  width: inherit;
  height: inherit;
}



.canvas-source-content {
  width: inherit;
  height: inherit;
}

.image-background {
  width: 30%;
  height: 30%;
  position: absolute;
  left: -500px;
  bottom: 10px;
  border-radius: 6px;
  z-index: 4;
}

.image-waiting-background {
  height: 100lvh;
  width: 100%;
}

.audio-image {
  width: 100%;
  height: 100%;
  position: absolute;
}

.audio-image-img {
  width: 100%;
  height: 100%;
}
</style>